import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { isSilent } from '../audio/silent';
import { alertState } from '../live/push';
import { useDialog } from './chrome';
import { Icon } from './icons';

// Ahillah's films (made with film/, from the app's own sky and screens): George's voice with captions, no
// music. Each starts from a tap, so the voice can play on a phone. The introduction is offered at the first
// visit and from Help; the sky calendar's film after the update that brought it, and from Help.

interface FilmSpec {
  dir: string;
  hls: string;
  /** HLS without the largest size, for phones (their screens are narrower than 1440 pixels). */
  hlsPhone?: string;
  hevc: string;
  avc: string;
  label: string;
  start: string;
  length: string;
  /** Offer "Watch again" and "Continue" at the end, rather than closing. */
  ending?: boolean;
  /** Under the start button: "Not now" closes the offer (for a film offered after an update). */
  later?: string;
}

const INTRO: FilmSpec = {
  dir: 'intro/', hls: 'hls/intro.m3u8', hevc: 'ahillah-intro-hevc.mp4', avc: 'ahillah-intro.mp4',
  label: 'Introduction to Ahillah', start: 'Watch the introduction', length: '65 seconds, with sound', later: 'Skip',
};
const CALENDAR: FilmSpec = {
  dir: 'promo/calendar/', hls: 'hls/master.m3u8', hlsPhone: 'hls/phone.m3u8', hevc: 'calendar-hevc.mp4', avc: 'calendar.mp4',
  label: 'The sky calendar', start: 'Watch the film', length: '74 seconds, with sound', ending: true, later: 'Not now',
};

const base = (spec: FilmSpec) => `${import.meta.env.BASE_URL}${spec.dir}`;
// iPhones and Macs stream it (HLS, in 2-second pieces, up to 4K): this server does not answer the partial
// requests Safari makes for a whole video file. Everything else plays an MP4, HEVC where it can. The address is
// set on the video itself: the video never reports a failed <source> child, so a missing film would hang.
function sourceFor(spec: FilmSpec): string {
  const probe = document.createElement('video');
  const narrow = Math.min(screen.width, screen.height) * devicePixelRatio <= 1440;
  if (/iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) && probe.canPlayType('application/vnd.apple.mpegurl') !== '') return `${base(spec)}${narrow && spec.hlsPhone ? spec.hlsPhone : spec.hls}`;
  return `${base(spec)}${probe.canPlayType('video/mp4; codecs="hvc1"') ? spec.hevc : spec.avc}`;
}

function Film({ spec, onDone, onLater }: { spec: FilmSpec; onDone: () => void; onLater?: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'start' | 'loading' | 'playing' | 'ended'>('start');
  const src = useMemo(() => sourceFor(spec), [spec]);
  // A modal layer over the whole app until it closes.
  useDialog(root, true);
  // Closing happens once: nothing from a pending play() or a late error may act after it.
  const open = useRef(true);
  useEffect(() => () => void (open.current = false), []);
  const close = (then: () => void) => {
    if (!open.current) return;
    open.current = false;
    then();
  };
  // Declining, skipping or a film that cannot play: for an update's film, its note shows instead.
  const decline = () => close(onLater ?? onDone);
  const play = () => {
    const v = video.current;
    if (!v) return decline();
    v.muted = isSilent();
    v.currentTime = 0;
    setState('loading');
    v.play().then(() => open.current && setState('playing'), decline);
  };
  // A film that has not started 12 seconds after the tap is treated as one that cannot load.
  useEffect(() => {
    if (state !== 'loading') return;
    const id = setTimeout(decline, 12000);
    return () => clearTimeout(id);
  }, [state]);
  // Once it is playing, a film that fails (a media error) or stops for 12 seconds without moving on (the connection
  // lost) closes the same way, rather than leaving a frozen picture.
  const stall = useRef<{ id: ReturnType<typeof setTimeout>; at: number } | null>(null);
  const moving = () => {
    if (stall.current) clearTimeout(stall.current.id);
    stall.current = null;
  };
  useEffect(() => moving, []);
  const waiting = () => {
    if (state === 'playing' && !stall.current) stall.current = { id: setTimeout(decline, 12000), at: video.current?.currentTime ?? 0 };
  };
  const progress = () => {
    if (stall.current && (video.current?.currentTime ?? 0) > stall.current.at) moving();
  };
  // Focus goes to the first button of each stage; Escape declines, or continues at the end.
  useEffect(() => {
    root.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (state === 'ended') close(onDone);
      else decline();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [state]);
  const ended = () => {
    moving();
    if (spec.ending) setState('ended');
    else close(onDone);
  };
  return (
    <div ref={root} class={`intro ${spec.ending ? 'intro-portrait' : ''}`} role="dialog" aria-modal="true" aria-label={spec.label}>
      <video ref={video} class="intro-video" src={src} playsInline preload="metadata" poster={`${base(spec)}poster.jpg`} onEnded={ended} onError={decline}
        onWaiting={waiting} onStalled={waiting} onPlaying={moving} onTimeUpdate={progress} />
      {state === 'playing' && <button class="intro-skip" onClick={decline}>Skip</button>}
      {(state === 'start' || state === 'loading') && (
        <div class="intro-start">
          <button class="btn big" onClick={play} disabled={state === 'loading'}><Icon name="play" size={19} /> {state === 'loading' ? 'Loading…' : spec.start}</button>
          <button class="btn-ghost" onClick={decline}>{spec.later ?? 'Skip'}</button>
          <p class="fine">{spec.length}</p>
        </div>
      )}
      {state === 'ended' && (
        <div class="intro-start intro-end">
          {alertState.value === 'on' && (
            <p class="intro-note">With notifications on, Ahillah now also tells you before major sky events. You can turn this off in Settings, under Sky events.</p>
          )}
          <button class="btn big" onClick={() => close(onDone)}>Continue to app</button>
          <button class="btn-ghost" onClick={play}><Icon name="play" size={17} /> Watch again</button>
        </div>
      )}
    </div>
  );
}

export const IntroFilm = ({ onDone }: { onDone: () => void }) => <Film spec={INTRO} onDone={onDone} />;
/** The sky calendar's film. `onLater`: what "Not now", Skip or a failure does when it is offered after the update
 *  (its note shows instead); `onDone`: Continue at the end. */
export const CalendarFilm = ({ onDone, onLater }: { onDone: () => void; onLater?: () => void }) => <Film spec={CALENDAR} onDone={onDone} onLater={onLater} />;
